import MorphingLoader from '@app/src/components/MorphingLoader'
import Avatar, { AvatarSize } from '@app/src/components/Ui/Avatar'
import { useAuthentication } from '@app/src/context/AuthenticationContext'
import useOrganizationSettings from '@app/src/hooks/useOrganizationSettings'
import { CONFIGURATION_PAGE_IDS } from '@app/src/pages/Configurations/ConfigurationsScene'
import { Roles } from '@app/src/wf-constants'
import paths from '@app/src/wf-constants/paths'
import { Box, Button, DialogContent, Stack, Typography, useTheme } from '@mui/material'
import React from 'react'
import { useIntl } from 'react-intl'
import { generatePath, useHistory } from 'react-router'
import { ViewTypeName } from '../../ManageRequests/ManageRequestsScene'

type ScheduleScreeningSuccessViewProps = {
  creditsUsed: number
  onClose: () => void
  hideViewScreenedRequests?: boolean
}

const ScheduleScreeningSuccessView: React.FC<ScheduleScreeningSuccessViewProps> = ({
  creditsUsed,
  onClose,
  hideViewScreenedRequests = false,
}) => {
  const { formatMessage } = useIntl()
  const history = useHistory()
  const { settings: organizationSettings } = useOrganizationSettings()
  const { scope } = useAuthentication()
  const isContributor = scope.role === Roles.Contributor
  const { palette } = useTheme()

  return (
    <DialogContent>
      <Stack height="100%" alignItems="center">
        <Stack flexGrow={1} alignItems="center" justifyContent="center" width={600} spacing={4}>
          <Avatar variant="circular" size={AvatarSize.XXXL} sx={{ bgcolor: 'communication.light' }}>
            <MorphingLoader size={56} color={palette.communication.dark} transitionDuration={400} />
          </Avatar>
          <Stack textAlign="center" spacing={1}>
            <Typography variant="h6">
              {formatMessage({ id: 'form.scheduleProviderScreening.success.screeningInProgress' })}
            </Typography>
            <Typography color="textSecondary">
              {formatMessage({ id: 'form.scheduleProviderScreening.success.screeningInProgressDescription' })}
            </Typography>
          </Stack>
          <Stack spacing={2} width="100%">
            <Stack direction="row" border="1px solid" borderColor="grey.300" borderRadius={2} p={2}>
              <Box flex={1} borderRight="1px solid" borderColor="grey.300">
                <Typography color="textSecondary" gutterBottom>
                  {formatMessage({ id: 'form.scheduleProviderScreening.success.creditsUsed' })}
                </Typography>
                <Typography variant="subtitle1">{creditsUsed.toLocaleString()}</Typography>
              </Box>
              <Box flex={1} pl={2}>
                <Typography color="textSecondary" gutterBottom>
                  {formatMessage({ id: 'form.scheduleProviderScreening.success.remainingCredits' })}
                </Typography>
                <Typography variant="subtitle1">
                  {((organizationSettings?.remainingProviderScreenings ?? 0) - creditsUsed).toLocaleString()}
                </Typography>
              </Box>
            </Stack>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography color="textSecondary">
                {formatMessage({ id: 'aiScreening.creditsOverview.oneCreditDescription' })}
              </Typography>
              {!isContributor && (
                <Button
                  variant="text"
                  onClick={() => {
                    onClose()
                    history.push(
                      generatePath(paths.configurations, {
                        configurationsPage: CONFIGURATION_PAGE_IDS.AiScreening,
                      }),
                    )
                  }}
                >
                  {formatMessage({ id: 'form.scheduleProviderScreening.success.viewLimit' })}
                </Button>
              )}
            </Stack>
          </Stack>
          <Stack spacing={2} pt={4}>
            <Button onClick={onClose}>{formatMessage({ id: 'general.close' })}</Button>
            {!hideViewScreenedRequests && (
              <Button
                variant="contained"
                onClick={() => {
                  onClose()
                  history.push(
                    generatePath(paths.manageRequest, {
                      view: ViewTypeName.ScreenedRequests,
                    }),
                  )
                }}
              >
                {formatMessage({ id: 'form.scheduleProviderScreening.success.viewScreenedRequests' })}
              </Button>
            )}
          </Stack>
        </Stack>
      </Stack>
    </DialogContent>
  )
}

export default ScheduleScreeningSuccessView
